import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
  type MutationFilters,
  type QueryClient,
} from '@tanstack/vue-query';
import { request } from '@/api/request';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  type MutationOptions,
  type QueryOptions,
} from '@/modules/query/utils';
import { refreshSupplierScopeEventually } from '@/modules/profile/api/complianceConsistency';
import {
  SUPPLIER_ASSESSMENT_ANSWERS_QUERY,
  SUPPLIER_ASSESSMENT_OVERRIDES_QUERY,
  SUPPLIER_ASSESSMENT_REMEDIATIONS_QUERY,
  SUPPLIER_ASSESSMENT_REVIEW_QUERY,
  SUPPLIER_ASSESSMENT_SCOPING_ANSWERS_QUERY,
  SUPPLIER_INFO_QUERY,
  SUPPLIER_PROFILE_SUMMARY_QUERY,
} from '@/modules/profile/api/queryKeys';
import {
  ASSOCIATED_DISCUSSION_QUERY,
  DISCUSSIONS_INBOX_QUERY,
} from '@/modules/discussions/api/queryKeys';
import type {
  AnswerSet,
  AnswerSetAnswer,
  AnswerSetRequirements,
} from '@@/packages/shared/types/models/answerSet';
import type { ScopingAnswer } from '@@/packages/shared/types/models/scopingAnswer';
import {
  ANSWER_SET_ANSWERS_QUERY,
  ANSWER_SET_QUERY,
  ANSWER_SET_REQUIREMENTS_QUERY,
  ANSWER_SET_SCOPING_ANSWERS_QUERY,
  START_ANSWER_SET_MUTATION,
  SWITCH_ACTIVE_ANSWER_SET_MUTATION,
} from './queryKeys';

export function useAnswerSetDetails(answerSetID: MaybeRefOrGetter<string>, options?: QueryOptions) {
  return useQuery(
    mergeQueryOptions(options, {
      queryKey: [ANSWER_SET_QUERY, toRef(answerSetID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<AnswerSet>('GET', `/assessments/answer-sets/${queryKey[1]}`);
        return res.data;
      },
    }),
  );
}

export async function fetchAnswerSetAnswers(answerSetID: string) {
  const res = await request<{ answers: AnswerSetAnswer[] }>(
    'GET',
    `/assessments/answer-sets/${answerSetID}/answers`,
  );
  return res.data.answers;
}

export function useAnswerSetAnswers(answerSetID: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [ANSWER_SET_ANSWERS_QUERY, toRef(answerSetID)] as const,
      queryFn: ({ queryKey }) => fetchAnswerSetAnswers(queryKey[1]),
    }),
  );

  const answers = computed(() => query.data.value ?? []);

  return { ...query, answers };
}

export async function fetchAnswerSetRequirements(answerSetID: string) {
  const res = await request<AnswerSetRequirements>(
    'GET',
    `/assessments/answer-sets/${answerSetID}/requirements`,
  );
  return res.data;
}

export function useAnswerSetRequirements(
  answerSetID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  return useQuery(
    mergeQueryOptions(options, {
      queryKey: [ANSWER_SET_REQUIREMENTS_QUERY, toRef(answerSetID)] as const,
      queryFn: ({ queryKey }) => fetchAnswerSetRequirements(queryKey[1]),
    }),
  );
}

export function useAnswerSetScopingAnswers(
  answerSetID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [ANSWER_SET_SCOPING_ANSWERS_QUERY, toRef(answerSetID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<{ scopingAnswers: ScopingAnswer[] }>(
          'GET',
          `/assessments/answer-sets/${queryKey[1]}/scoping-answers`,
        );
        return res.data.scopingAnswers;
      },
    }),
  );

  const scopingAnswers = computed(() => query.data.value ?? []);

  return { ...query, scopingAnswers };
}

// Every write names the set's supplier as well as the set, because the client
// review reads the supplier's active set through queries keyed by supplier ID.
type AnswerSetTarget = {
  answerSetID: string;
  supplierID: string;
};

type UpdateAnswerSetAnswerVariables = AnswerSetTarget & {
  controlID: string;
  answer: string;
  notes: string;
  // The answer's evidence. Left out, the answer keeps what it has. Sent, it is the
  // whole list, so an empty one removes it all.
  documentIDs?: string[];
};

type DeleteAnswerSetAnswerVariables = AnswerSetTarget & {
  controlID: string;
};

type UpdateAnswerSetScopingAnswerVariables = AnswerSetTarget & {
  domainID: string;
  answer: boolean;
  notes: string;
};

// Writes answer 204 with no body, and a save can cascade not-applicable onto
// child controls, so the set's answers and progress are always read again.
function invalidateAnswers(queryClient: QueryClient, { answerSetID, supplierID }: AnswerSetTarget) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: [ANSWER_SET_ANSWERS_QUERY, answerSetID] }),
    queryClient.invalidateQueries({ queryKey: [ANSWER_SET_QUERY, answerSetID] }),
    queryClient.invalidateQueries({ queryKey: [SUPPLIER_ASSESSMENT_ANSWERS_QUERY, supplierID] }),
    queryClient.invalidateQueries({ queryKey: [SUPPLIER_INFO_QUERY, supplierID] }),
  ]);
}

export function useUpdateAnswerSetAnswer(
  options?: MutationOptions<void, UpdateAnswerSetAnswerVariables>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ answerSetID, controlID, answer, notes, documentIDs }) {
        await request<null>(
          'PATCH',
          `/assessments/answer-sets/${answerSetID}/answers/${controlID}`,
          { answer, notes, documentIDs },
        );
      },
      async onSuccess(_data, variables) {
        await invalidateAnswers(queryClient, variables);
      },
    }),
  );
}

export function useDeleteAnswerSetAnswer(
  options?: MutationOptions<void, DeleteAnswerSetAnswerVariables>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ answerSetID, controlID }) {
        await request<null>(
          'DELETE',
          `/assessments/answer-sets/${answerSetID}/answers/${controlID}`,
        );
      },
      async onSuccess(_data, variables) {
        await invalidateAnswers(queryClient, variables);
      },
    }),
  );
}

export function useUpdateAnswerSetScopingAnswer(
  options?: MutationOptions<void, UpdateAnswerSetScopingAnswerVariables>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ answerSetID, domainID, answer, notes }) {
        await request<null>(
          'PATCH',
          `/assessments/answer-sets/${answerSetID}/scoping-answers/${domainID}`,
          { answer, notes },
        );
      },
      // Scoping a domain out changes which of its controls count, so the
      // answers and progress move with it.
      async onSuccess(_data, variables) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [ANSWER_SET_SCOPING_ANSWERS_QUERY, variables.answerSetID],
          }),
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_ASSESSMENT_SCOPING_ANSWERS_QUERY, variables.supplierID],
          }),
          invalidateAnswers(queryClient, variables),
        ]);
      },
    }),
  );
}

type StartAnswerSetVariables = {
  connectionID: string;
  supplierID: string;
};

// The client review reads the connection's active set through queries keyed by
// supplier ID, so these follow whichever set the connection now reads. The backend
// withholds overrides, remediations, discussions and the AI review and summary
// while the client set is active, so those refetch too. Discussions are keyed by
// their filters or thread rather than the supplier, so all of them go.
function invalidateActiveSet(queryClient: QueryClient, supplierID: string) {
  return Promise.all([
    ...[
      SUPPLIER_INFO_QUERY,
      SUPPLIER_ASSESSMENT_ANSWERS_QUERY,
      SUPPLIER_ASSESSMENT_SCOPING_ANSWERS_QUERY,
      SUPPLIER_ASSESSMENT_REMEDIATIONS_QUERY,
      SUPPLIER_ASSESSMENT_OVERRIDES_QUERY,
      SUPPLIER_ASSESSMENT_REVIEW_QUERY,
      SUPPLIER_PROFILE_SUMMARY_QUERY,
    ].map((key) => queryClient.invalidateQueries({ queryKey: [key, supplierID] })),
    queryClient.invalidateQueries({ queryKey: [DISCUSSIONS_INBOX_QUERY] }),
    queryClient.invalidateQueries({ queryKey: [ASSOCIATED_DISCUSSION_QUERY] }),
  ]);
}

// A first start makes the new set the one the connection reads. A repeat start
// answers 200 with the existing set and leaves the active set as it was.
export function useStartAnswerSet(options?: MutationOptions<AnswerSet, StartAnswerSetVariables>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      mutationKey: [START_ANSWER_SET_MUTATION],
      async mutationFn({ connectionID }) {
        const res = await request<AnswerSet>('POST', '/assessments/answer-sets', { connectionID });
        return res.data;
      },
      async onSuccess(answerSet, { supplierID }) {
        queryClient.setQueryData([ANSWER_SET_QUERY, answerSet.id], answerSet);
        await invalidateActiveSet(queryClient, supplierID);
      },
    }),
  );
}

// A start stays pending until its caller's onSuccess settles, so a caller that
// navigates there can keep the pre-start view up until the new set has opened.
export function useIsStartingAnswerSet() {
  const startsInFlight = useIsMutating({ mutationKey: [START_ANSWER_SET_MUTATION] });
  return computed(() => startsInFlight.value > 0);
}

// A start or switch stays pending until the client review has refetched, so while one
// is in flight the supplier's cached info can still name the set it is leaving.
export function changingActiveSetFilters(supplierID: string): MutationFilters {
  return {
    predicate: (mutation) => {
      const [key] = mutation.options.mutationKey ?? [];
      const { variables } = mutation.state;
      return (
        (key === START_ANSWER_SET_MUTATION || key === SWITCH_ACTIVE_ANSWER_SET_MUTATION) &&
        typeof variables === 'object' &&
        variables !== null &&
        'supplierID' in variables &&
        variables.supplierID === supplierID
      );
    },
  };
}

export function useIsChangingActiveSet(supplierID: MaybeRefOrGetter<string>) {
  // The ID is read in the getter rather than the predicate, so a change of supplier
  // re-counts at once instead of at the next mutation event.
  const changesInFlight = useIsMutating(() => changingActiveSetFilters(toValue(supplierID)));
  return computed(() => changesInFlight.value > 0);
}

export function useCompleteAnswerSet(options?: MutationOptions<void, AnswerSetTarget>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ answerSetID }) {
        await request<null>('PATCH', `/assessments/answer-sets/${answerSetID}`, {
          status: 'completed',
        });
      },
      async onSuccess(_data, { answerSetID, supplierID }) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [ANSWER_SET_QUERY, answerSetID] }),
          queryClient.invalidateQueries({ queryKey: [SUPPLIER_INFO_QUERY, supplierID] }),
        ]);
        // Completion queues the compliance calculation rather than returning a score
        refreshSupplierScopeEventually(queryClient, supplierID);
      },
    }),
  );
}

type SwitchActiveAnswerSetVariables = AnswerSetTarget & {
  connectionID: string;
};

// Points the connection at its client set or the supplier's own set, and
// answers with how many answers the connection reads after the switch.
export function useSwitchActiveAnswerSet(
  options?: MutationOptions<{ answerCount: number }, SwitchActiveAnswerSetVariables>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      mutationKey: [SWITCH_ACTIVE_ANSWER_SET_MUTATION],
      async mutationFn({ connectionID, answerSetID }) {
        const res = await request<{ answerCount: number }>(
          'PATCH',
          '/assessments/active-answer-sets',
          { connectionID, answerSetID },
        );
        return res.data;
      },
      async onSuccess(_data, { supplierID }) {
        await invalidateActiveSet(queryClient, supplierID);
        // The switch queues the compliance calculation rather than returning a score
        refreshSupplierScopeEventually(queryClient, supplierID);
      },
    }),
  );
}
